/**
 * /try Speculative Ingest Controller.
 *
 * WHAT THIS FILE DOES:
 * - Manages speculative URL ingest state and UI locks.
 * - Debounces URL detection while the user types.
 * - Keeps /try UI responsive by preventing mixed inputs.
 *
 * WHY THIS EXISTS:
 * - Keeps try-ui.ts focused on DOM orchestration (no god module).
 * - Centralizes speculation state transitions so try-ui.ts stays lean.
 *
 * CALLED BY:
 * - src/client/try-ui.ts (textarea, file, and auth handlers)
 *
 * @file src/client/try-speculation.ts
 */

import type {
  IngestResponse,
  TryMediaClientTranscodeTelemetry
} from './try-shared.js';
import type { WebTryClient } from './try-client.js';
import { buildTryMediaTelemetryEvents, TryMediaTelemetrySources } from './try-media-telemetry.js';
import { track as trackWebEvent } from './web-telemetry.js';
import { resolveMediaContentType, resolvePdfContentType } from './try-utils.js';
import { extractSingleHttpUrl, normalizeUrlIdentity } from '../lib/url-input.js';
import { GistPipelineStatusLabels } from '@gist-protocol/core/shared/gist-pipeline-status.js';

const SpeculationTiming = Object.freeze({
  /**
   * Debounce window for URL detection while typing.
   * Keeps us from spamming speculative work on rapid edits.
   */
  URL_DEBOUNCE_MS: 500,

  /**
   * How long to show the "single input" toast before auto-dismiss.
   */
  TOAST_AUTO_DISMISS_MS: 3_000
});

const SpeculationCopy = Object.freeze({
  /** Inline helper text while speculative work is active. */
  PREPARING: GistPipelineStatusLabels.LOADING,
  /** Toast message when user tries to mix inputs. */
  SINGLE_INPUT: 'Gist can only handle one item at a time.'
});

type SpeculationKind = 'url' | 'pdf' | 'media';
type FileSpeculationKind = 'pdf' | 'media';

/**
 * Best-effort abort detection across browsers/environments.
 */
function isAbortError(error: unknown): boolean {
  return (error instanceof DOMException && error.name === 'AbortError')
    || (error instanceof Error && error.name === 'AbortError');
}

/**
 * Tracks speculative ingest state for /try.
 *
 * State transitions:
 * - idle → pending when speculative work starts
 * - pending → done on success
 * - pending → failed on error or cancellation
 * - any → idle when resetSpeculation() is called
 */
interface SpeculativeState {
  status: 'idle' | 'pending' | 'done' | 'failed';
  kind?: SpeculationKind;
  response?: IngestResponse;
  uploadId?: string;
  fileName?: string;
  contentType?: string;
  promise?: Promise<void>;
  abortController?: AbortController;
  inputHash?: string;
  speculationId?: number;
}

/**
 * Resolved file upload metadata from speculation.
 */
interface SpeculativeUploadInfo {
  kind: FileSpeculationKind;
  uploadId: string;
  fileName: string;
  contentType: string;
}

export interface SpeculationDom {
  helperText: HTMLParagraphElement | null;
  uploadBtn: HTMLButtonElement | null;
  fileInput: HTMLInputElement | null;
  textarea: HTMLTextAreaElement | null;
  toast: HTMLDivElement | null;
}

/**
 * Speculative ingest controller for /try.
 */
export class TrySpeculationController {
  private readonly dom: SpeculationDom;
  private readonly client: WebTryClient;
  private readonly getGistLanguage: () => string;
  private speculativeState: SpeculativeState = { status: 'idle' };
  private speculationIdCounter = 0;
  private urlSpeculationDebounceId: number | null = null;
  private toastDismissTimeoutId: number | null = null;
  private helperDefaultText = '';
  private lastTextareaValue = '';

  constructor(params: { dom: SpeculationDom; client: WebTryClient; getGistLanguage: () => string }) {
    this.dom = params.dom;
    this.client = params.client;
    this.getGistLanguage = params.getGistLanguage;
  }

  /**
   * Captures helper text defaults and syncs the initial speculation UI.
   */
  initialize(): void {
    if (this.dom.helperText) {
      this.helperDefaultText = this.dom.helperText.textContent?.trim() ?? '';
    }
    this.updateSpeculationUi();
  }

  /**
   * Replaces the captured helper-text default so the next idle UI update
   * keeps the active preset's copy instead of restoring the SSR default.
   *
   * Called by `TryUIController.applyCreatePreset` whenever the create-module
   * tab changes — prevents speculation's idle path from stomping on the
   * preset helper after a URL field is cleared, etc.
   */
  setHelperDefaultText(text: string): void {
    this.helperDefaultText = text;
    this.updateSpeculationUi();
  }

  /**
   * Handles textarea input with speculation-aware guards + URL detection.
   *
   * @param value - Current textarea value.
   * @param hasFile - Whether a file is attached.
   * @returns True when the input was blocked by speculation rules.
   */
  handleTextareaInput(value: string, hasFile: boolean): boolean {
    if (this.isSpeculationBlockingTextareaInput()) {
      this.showSingleInputToast();
      return true;
    }

    this.lastTextareaValue = value;
    this.abortUrlSpeculationIfChanged(value);
    this.resetUrlSpeculationIfInputChanged(value);
    this.scheduleUrlSpeculation(hasFile);
    return false;
  }

  /**
   * Blocks keyboard edits when speculation locks the textarea.
   *
   * @param event - Keyboard event.
   * @returns True when the edit should be blocked.
   */
  handleTextareaLockedKeydown(event: KeyboardEvent): boolean {
    if (!this.isSpeculationBlockingTextareaInput()) {
      return false;
    }

    if (event.metaKey || event.ctrlKey) {
      return false;
    }

    const isModifier = event.key === 'Shift'
      || event.key === 'Alt'
      || event.key === 'Meta'
      || event.key === 'Control'
      || event.key === 'Tab'
      || event.key.startsWith('Arrow');

    if (!isModifier) {
      this.showSingleInputToast();
      return true;
    }

    return false;
  }

  /**
   * Blocks paste when speculation locks the textarea.
   *
   * @returns True when paste should be blocked.
   */
  handleTextareaLockedPaste(): boolean {
    if (!this.isSpeculationBlockingTextareaInput()) {
      return false;
    }
    this.showSingleInputToast();
    return true;
  }

  /**
   * Handles file attachment attempts, respecting speculation locks.
   *
   * @returns True when attachment should be blocked.
   */
  handleFileAttachmentAttempt(): boolean {
    if (this.isSpeculationBlockingFileInput()) {
      this.showSingleInputToast();
      return true;
    }

    this.resetSpeculation('file_attach');
    return false;
  }

  /**
   * Notifies the user that another input is currently locked.
   */
  notifyInputConflict(): void {
    this.showSingleInputToast();
  }

  /**
   * Resets speculation when auth state changes.
   */
  resetForAuthChange(): void {
    this.resetSpeculation('auth_changed');
  }

  /**
   * Resolves a speculative URL ingest response when available.
   */
  async resolveSpeculativeUrlResponse(content: string): Promise<IngestResponse | null> {
    const url = this.extractSingleUrl(content);
    if (!url) return null;

    const inputHash = this.buildUrlInputHash(url);
    if (this.speculativeState.kind !== 'url' || this.speculativeState.inputHash !== inputHash) {
      return null;
    }

    if (
      this.speculativeState.status === 'done'
      && this.speculativeState.response
      && this.isCompletedUrlIngestResponse(this.speculativeState.response)
    ) {
      return this.speculativeState.response;
    }

    if (this.speculativeState.status === 'pending' && this.speculativeState.promise) {
      const speculationId = this.speculativeState.speculationId;
      try {
        await this.speculativeState.promise;
        if (!speculationId || !this.isSpeculationCurrent(speculationId)) {
          return null;
        }
        const currentState = this.speculativeState;
        if (
          currentState.status === 'done'
          && currentState.response
          && this.isCompletedUrlIngestResponse(currentState.response)
        ) {
          return currentState.response;
        }
        return null;
      } catch {
        return null;
      }
    }

    return null;
  }

  /**
   * Starts speculative file upload + extraction (PDF) when a file is attached.
   */
  startFileSpeculation(file: File, options: { durationMs?: number | null } = {}): void {
    const inputHash = this.buildFileInputHash(file);
    const resolvedKind = this.resolveFileSpeculationKind(file);

    if (!resolvedKind) {
      this.speculativeState = {
        status: 'failed',
        kind: 'media',
        inputHash,
        speculationId: this.nextSpeculationId(),
        fileName: file.name,
        contentType: file.type || 'application/octet-stream'
      };
      this.updateSpeculationUi();
      return;
    }

    const speculationId = this.nextSpeculationId();
    const abortController = new AbortController();
    const promise = resolvedKind.kind === 'pdf'
      ? this.runPdfSpeculation({
        file,
        inputHash,
        speculationId,
        abortController
      })
      : this.runMediaSpeculation({
        file,
        durationMs: options.durationMs,
        inputHash,
        speculationId,
        abortController
      });

    this.speculativeState = {
      status: 'pending',
      kind: resolvedKind.kind,
      promise,
      abortController,
      inputHash,
      speculationId
    };

    this.updateSpeculationUi();
  }

  /**
   * Clears speculation state after removing a file.
   */
  handleFileRemoved(): void {
    this.resetSpeculation('file_removed');
  }

  /**
   * Resolves a speculative file upload when available.
   */
  async resolveSpeculativeFileUpload(file: File): Promise<SpeculativeUploadInfo | null> {
    const inputHash = this.buildFileInputHash(file);

    if (!this.isMatchingFileSpeculation(inputHash)) {
      return null;
    }

    if (this.speculativeState.status === 'done') {
      const info = this.buildSpeculativeUploadInfo();
      if (info) {
        return info;
      }
    }

    if (this.speculativeState.status === 'pending' && this.speculativeState.promise) {
      const speculationId = this.speculativeState.speculationId;
      try {
        await this.speculativeState.promise;
        if (!speculationId || !this.isSpeculationCurrent(speculationId)) {
          return null;
        }
        const currentState = this.speculativeState;
        if (currentState.status === 'done') {
          const info = this.buildSpeculativeUploadInfo();
          if (info) {
            return info;
          }
        }
        return null;
      } catch {
        return null;
      }
    }

    return null;
  }

  /**
   * Returns true when a speculative state should block file input.
   */
  isSpeculationBlockingFileInput(): boolean {
    return this.speculativeState.status === 'pending' || this.isFileActive();
  }

  /**
   * Returns true when a speculative state should block textarea edits.
   */
  isSpeculationBlockingTextareaInput(): boolean {
    return this.isFileActive();
  }

  /**
   * Checks if a string looks like a URL.
   */
  isUrl(value: string): boolean {
    return Boolean(this.extractSingleUrl(value));
  }

  /**
   * Debounces URL detection for speculative work.
   */
  private scheduleUrlSpeculation(hasFile: boolean): void {
    if (hasFile) return;

    if (this.urlSpeculationDebounceId) {
      window.clearTimeout(this.urlSpeculationDebounceId);
    }

    this.urlSpeculationDebounceId = window.setTimeout(() => {
      this.runUrlSpeculationFromTextarea();
    }, SpeculationTiming.URL_DEBOUNCE_MS);
  }

  /**
   * Starts speculative URL handling once debounce settles.
   */
  private runUrlSpeculationFromTextarea(): void {
    if (this.urlSpeculationDebounceId != null) {
      window.clearTimeout(this.urlSpeculationDebounceId);
      this.urlSpeculationDebounceId = null;
    }

    const url = this.extractSingleUrl(this.lastTextareaValue);

    if (!url) {
      if (this.speculativeState.kind === 'url') {
        this.resetSpeculation('url_cleared');
      }
      return;
    }

    const inputHash = this.buildUrlInputHash(url);
    if (this.speculativeState.kind === 'url' && this.speculativeState.inputHash === inputHash) {
      return;
    }

    this.resetSpeculation('url_changed');
    this.startUrlSpeculation(url);
  }

  /**
   * Begins a speculative URL ingest.
   */
  private startUrlSpeculation(url: string): void {
    const speculationId = this.nextSpeculationId();
    const abortController = new AbortController();
    const inputHash = this.buildUrlInputHash(url);

    const promise = this.runUrlSpeculation({
      url,
      inputHash,
      speculationId,
      abortController
    });

    this.speculativeState = {
      status: 'pending',
      kind: 'url',
      promise,
      abortController,
      inputHash,
      speculationId
    };

    this.updateSpeculationUi();
  }

  /**
   * Executes speculative URL ingest and updates state.
   */
  private async runUrlSpeculation(params: {
    url: string;
    inputHash: string;
    speculationId: number;
    abortController: AbortController;
  }): Promise<void> {
    try {
      const response = await this.client.speculativeIngestUrl({
        url: params.url,
        gistLanguage: this.getGistLanguage(),
        signal: params.abortController.signal,
        speculativeInstantOnly: true
      });

      if (!this.isSpeculationCurrent(params.speculationId)) {
        return;
      }

      this.speculativeState = {
        status: 'done',
        kind: 'url',
        response,
        inputHash: params.inputHash,
        speculationId: params.speculationId
      };
      this.updateSpeculationUi();
    } catch {
      if (!this.isSpeculationCurrent(params.speculationId)) {
        return;
      }

      this.speculativeState = {
        status: 'failed',
        kind: 'url',
        inputHash: params.inputHash,
        speculationId: params.speculationId
      };
      this.updateSpeculationUi();
    }
  }

  /**
   * Executes speculative PDF upload + extraction and updates state.
   */
  private async runPdfSpeculation(params: {
    file: File;
    inputHash: string;
    speculationId: number;
    abortController: AbortController;
  }): Promise<void> {
    try {
      const upload = await this.client.initAndUploadPdf({
        file: params.file,
        signal: params.abortController.signal
      });

      if (!this.isSpeculationCurrent(params.speculationId)) {
        return;
      }

      try {
        await this.client.extractPdf({
          uploadId: upload.uploadId,
          signal: params.abortController.signal
        });
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }
      }

      if (!this.isSpeculationCurrent(params.speculationId)) {
        return;
      }

      this.speculativeState = {
        status: 'done',
        kind: 'pdf',
        uploadId: upload.uploadId,
        fileName: upload.fileName,
        contentType: upload.contentType,
        inputHash: params.inputHash,
        speculationId: params.speculationId
      };
      this.updateSpeculationUi();
    } catch {
      if (!this.isSpeculationCurrent(params.speculationId)) {
        return;
      }

      this.speculativeState = {
        status: 'failed',
        kind: 'pdf',
        inputHash: params.inputHash,
        speculationId: params.speculationId
      };
      this.updateSpeculationUi();
    }
  }

  /**
   * Executes speculative media upload and updates state.
   *
   * Uses the same client-audio-first upload method as submit flow so
   * speculation and final ingest follow identical transcode/fallback routing.
   */
  private async runMediaSpeculation(params: {
    file: File;
    durationMs?: number | null;
    inputHash: string;
    speculationId: number;
    abortController: AbortController;
  }): Promise<void> {
    try {
      const upload = await this.client.initAndUploadMediaClientFirst({
        file: params.file,
        durationMs: typeof params.durationMs === 'number' ? params.durationMs : undefined,
        signal: params.abortController.signal,
        onTranscodeDecision: (telemetry) => {
          if (!this.isSpeculationCurrent(params.speculationId)) {
            return;
          }
          this.emitMediaTelemetry(telemetry);
        }
      });

      if (!this.isSpeculationCurrent(params.speculationId)) {
        return;
      }

      this.speculativeState = {
        status: 'done',
        kind: 'media',
        uploadId: upload.uploadId,
        fileName: upload.fileName,
        contentType: upload.contentType,
        inputHash: params.inputHash,
        speculationId: params.speculationId
      };

      this.updateSpeculationUi();
    } catch (error) {
      // A-039: Surface speculation errors in dev tools for debuggability.
      console.warn('[try-speculation] Media speculation failed:', error);

      if (!this.isSpeculationCurrent(params.speculationId)) {
        return;
      }

      this.speculativeState = {
        status: 'failed',
        kind: 'media',
        inputHash: params.inputHash,
        speculationId: params.speculationId
      };
      this.updateSpeculationUi();
    }
  }

  /**
   * Resets URL speculation immediately when the user edits and the current
   * URL no longer matches the last speculative result (done/failed).
   */
  private resetUrlSpeculationIfInputChanged(value: string): void {
    if (this.speculativeState.kind !== 'url') return;
    if (this.speculativeState.status !== 'done' && this.speculativeState.status !== 'failed') {
      return;
    }

    const url = this.extractSingleUrl(value);
    const inputHash = url ? this.buildUrlInputHash(url) : '';
    const lastHash = this.speculativeState.inputHash ?? '';
    if (inputHash !== lastHash) {
      this.resetSpeculation('url_changed');
    }
  }

  /**
   * Aborts URL speculation when textarea edits change the URL.
   */
  private abortUrlSpeculationIfChanged(value: string): void {
    if (this.speculativeState.kind !== 'url' || this.speculativeState.status !== 'pending') {
      return;
    }
    const url = this.extractSingleUrl(value);
    const inputHash = url ? this.buildUrlInputHash(url) : '';
    if (!inputHash || inputHash !== this.speculativeState.inputHash) {
      this.resetSpeculation('url_edit');
    }
  }

  /**
   * Resets speculative state and unlocks the UI.
   */
  private resetSpeculation(_reason: string): void {
    if (this.urlSpeculationDebounceId) {
      window.clearTimeout(this.urlSpeculationDebounceId);
      this.urlSpeculationDebounceId = null;
    }
    if (this.speculativeState.abortController) {
      try {
        this.speculativeState.abortController.abort();
      } catch {
        // best-effort
      }
    }

    this.speculativeState = { status: 'idle' };
    this.updateSpeculationUi();
    this.clearToast();
  }

  /**
   * Applies speculation state to the input UI.
   */
  private updateSpeculationUi(): void {
    const isPending = this.speculativeState.status === 'pending';
    const isFileActive = this.isFileActive();

    this.setUploadButtonLocked(isPending || isFileActive);
    this.setTextareaLocked(isFileActive);
    this.setHelperText(isPending ? SpeculationCopy.PREPARING : this.helperDefaultText, isPending);
  }

  /**
   * Toggles the upload button lock state.
   */
  private setUploadButtonLocked(locked: boolean): void {
    const { uploadBtn, fileInput } = this.dom;
    if (!uploadBtn || !fileInput) return;
    uploadBtn.setAttribute('aria-disabled', locked ? 'true' : 'false');
    uploadBtn.classList.toggle('is-locked', locked);
    fileInput.disabled = locked;
  }

  /**
   * Toggles textarea readonly state when input is locked.
   */
  private setTextareaLocked(locked: boolean): void {
    const { textarea } = this.dom;
    if (!textarea) return;
    textarea.readOnly = locked;
    textarea.setAttribute('aria-readonly', locked ? 'true' : 'false');
  }

  /**
   * Updates helper copy beneath the input.
   */
  private setHelperText(message: string, isSpeculating: boolean): void {
    const { helperText } = this.dom;
    if (!helperText) return;
    helperText.textContent = message;
    helperText.classList.toggle('is-speculating', isSpeculating);
  }

  /**
   * Shows the single-input enforcement toast.
   */
  private showSingleInputToast(): void {
    this.showToast(SpeculationCopy.SINGLE_INPUT);
  }

  /**
   * Shows a brief inline toast message.
   */
  private showToast(message: string): void {
    const { toast } = this.dom;
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('visible');

    if (this.toastDismissTimeoutId) {
      window.clearTimeout(this.toastDismissTimeoutId);
    }

    this.toastDismissTimeoutId = window.setTimeout(() => {
      this.clearToast();
    }, SpeculationTiming.TOAST_AUTO_DISMISS_MS);
  }

  /**
   * Clears the inline toast message.
   */
  private clearToast(): void {
    const { toast } = this.dom;
    if (!toast) return;
    if (this.toastDismissTimeoutId) {
      window.clearTimeout(this.toastDismissTimeoutId);
      this.toastDismissTimeoutId = null;
    }
    toast.textContent = '';
    toast.classList.remove('visible');
  }

  /**
   * Emits media client-transcode telemetry for speculative upload routing.
   */
  private emitMediaTelemetry(telemetry: TryMediaClientTranscodeTelemetry): void {
    const events = buildTryMediaTelemetryEvents({
      source: TryMediaTelemetrySources.SPECULATION,
      telemetry
    });
    const authState = this.client.getAuthStateLabel();
    for (const event of events) {
      trackWebEvent(event.event, { ...(event.metadata ?? {}), authState });
    }
  }

  /**
   * Returns true when a file is attached and should lock textarea edits.
   *
   * `failed` states are treated like `idle` here: once speculation fails,
   * the file may be unusable and the user should be able to type a URL or
   * text as a recovery path without first clicking Remove. Pending and done
   * states still lock (file is the active input).
   */
  private isFileActive(): boolean {
    if (this.speculativeState.status === 'idle' || this.speculativeState.status === 'failed') {
      return false;
    }
    return this.speculativeState.kind !== undefined
      && this.speculativeState.kind !== 'url';
  }

  /**
   * Returns true when a speculative URL ingest resolved to a completed payload.
   *
   * Failed speculative responses intentionally fall back to interactive submit.
   */
  private isCompletedUrlIngestResponse(response: IngestResponse): boolean {
    return typeof response.contentId === 'string' && response.contentId.trim().length > 0;
  }

  /**
   * Resolves file speculation kind + content type for uploads.
   */
  private resolveFileSpeculationKind(file: File): { kind: FileSpeculationKind; contentType: string } | null {
    const pdfType = resolvePdfContentType(file);
    if (pdfType) {
      return { kind: 'pdf', contentType: pdfType };
    }

    const mediaType = resolveMediaContentType(file);
    if (mediaType) {
      return { kind: 'media', contentType: mediaType };
    }

    return null;
  }

  /**
   * Builds a stable hash for a file input to detect replacements.
   */
  private buildFileInputHash(file: File): string {
    const name = file.name ?? '';
    const size = Number.isFinite(file.size) ? file.size : 0;
    const lastModified = Number.isFinite(file.lastModified) ? file.lastModified : 0;
    const type = file.type ?? '';
    return `${name}::${size}::${lastModified}::${type}`;
  }

  /**
   * Returns true when the current speculative state matches a file hash.
   */
  private isMatchingFileSpeculation(inputHash: string): boolean {
    return this.speculativeState.kind !== undefined
      && this.speculativeState.kind !== 'url'
      && this.speculativeState.inputHash === inputHash;
  }

  /**
   * Builds a SpeculativeUploadInfo payload from the current state.
   */
  private buildSpeculativeUploadInfo(): SpeculativeUploadInfo | null {
    if (this.speculativeState.kind !== 'pdf' && this.speculativeState.kind !== 'media') {
      return null;
    }

    if (!this.speculativeState.uploadId || !this.speculativeState.fileName || !this.speculativeState.contentType) {
      return null;
    }

    return {
      kind: this.speculativeState.kind,
      uploadId: this.speculativeState.uploadId,
      fileName: this.speculativeState.fileName,
      contentType: this.speculativeState.contentType
    };
  }

  /**
   * Generates a deterministic hash for a URL input.
   */
  private buildUrlInputHash(url: string): string {
    return normalizeUrlIdentity(url);
  }

  /**
   * Extracts a single-line HTTP(S) URL from user input.
   */
  private extractSingleUrl(value: string): string | null {
    return extractSingleHttpUrl(value);
  }

  /**
   * Returns the next speculation ID for stale-response guards.
   */
  private nextSpeculationId(): number {
    this.speculationIdCounter += 1;
    return this.speculationIdCounter;
  }

  /**
   * Checks whether a speculation ID is still current.
   */
  private isSpeculationCurrent(speculationId: number): boolean {
    return this.speculativeState.speculationId === speculationId;
  }
}
